import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import ProcessLine from "@/components/ProcessLine";
import { processSteps } from "@/data/capabilities";
import { technologies, metrics } from "@/data/technologies";
import { usePageMeta } from "@/hooks/usePageMeta";

const ROTATING = ["biochar.", "bio-oil.", "bio-coal.", "CBG.", "electricity.", "carbon removal."];

const Hero = () => (
  <section
    data-testid="home-hero"
    className="relative min-h-[76svh] flex flex-col bg-paper text-char-900 overflow-hidden"
  >
    <div className="relative flex-1 flex flex-col justify-end">
      <div className="max-w-[1400px] mx-auto w-full px-6 lg:px-10 pt-44 pb-20 lg:pb-24">
        <ScrollReveal delay={80}>
          <h1
            data-testid="hero-headline"
            className="display-huge mt-8 text-[clamp(48px,9vw,128px)] text-char-900 max-w-[18ch] md:max-w-none"
          >
            <span className="md:whitespace-nowrap">We help turn biomass into</span>
            <span style={{ display: "block" }}>
              <span
                className="rotating-word align-baseline"
                style={{ display: "inline-block", minWidth: "12ch" }}
              >
                {ROTATING.map((w, i) => (
                  <span key={i}>{w}</span>
                ))}
                <span className="ghost">carbon removal.</span>
              </span>
            </span>
            <span
              className="font-display text-heading whitespace-nowrap"
              style={{ display: "block" }}
            >
              End to end.
            </span>
          </h1>
        </ScrollReveal>

        <ScrollReveal delay={220}>
          <p className="mt-8 max-w-[62ch] text-lg md:text-xl text-char-700 leading-relaxed">
            We make world-class biomass equipment.
            <br />
            We deliver cost-effective MRV solutions.
            <br />
            We provide practical advisory support.
          </p>
        </ScrollReveal>

        <ScrollReveal delay={340}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-cta-primary" className="btn-ember">
              Discuss Your Project
              <ArrowUpRight size={18} />
            </Link>
            <Link to="/equipment" data-testid="hero-cta-secondary" className="btn-ghost-light">
              Explore Our Technologies
              <ArrowRight size={16} />
            </Link>
          </div>
        </ScrollReveal>
      </div>
    </div>
  </section>
);

const TechShowcase = () => (
  <section
    data-testid="home-technologies"
    className="bg-paper py-20 lg:py-24 border-t border-char-900/10"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-7">
          <h2 className="font-display text-char-900">
            Technology built for real-world biomass.
          </h2>
        </div>
        <div className="md:col-span-4 md:col-start-9 flex md:justify-end">
          <Link
            to="/equipment"
            data-testid="home-tech-explore-all"
            className="inline-flex items-center gap-2 text-char-900 font-display text-lg hover:text-ember transition-colors"
          >
            Explore All Technologies
            <ArrowRight size={18} />
          </Link>
        </div>
      </div>

      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {technologies.map((t, i) => (
          <ScrollReveal key={t.number} delay={i * 60} className="h-full">
            <Link
              to={t.href}
              data-testid={`home-tech-card-${t.number}`}
              className="h-full block"
            >
              <div className="h-full flex flex-col border border-char-900/10 bg-paper hover:border-char-900/25 transition-colors p-6">
                <div className="flex items-start justify-between gap-3">
                  <span className="numeral text-2xl">{t.number}</span>
                </div>
                <h3 className="mt-5 font-display text-char-900 leading-tight">{t.title}</h3>
                <p className="mt-3 text-char-700 text-[15px] leading-relaxed line-clamp-3">
                  {t.body}
                </p>
              </div>
            </Link>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
);

const WHY_POINTS = [
  {
    title: "Operator-built",
    body: "Designed and commissioned by people who have run commercial pyrolysis plants.",
  },
  {
    title: "Carbon-ready from day one",
    body: "Plant data flows straight into Convergent, our MRV platform.",
  },
  {
    title: "Every output earns",
    body: "Biochar, bio-oil, syngas and heat, all engineered into revenue.",
  },
  {
    title: "The right technology for you",
    body: "Pyrolysis, torrefaction, gasification or CBG. We pick what fits.",
  },
];

const WhyTanren = () => (
  <section
    data-testid="home-why-tanren"
    className="bg-char-900 text-paper py-20 lg:py-24"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-10 lg:gap-14 items-start">
      <ScrollReveal className="md:col-span-5">
        <h2 className="font-display">Why Tanren</h2>
        <p className="mt-5 text-lg md:text-xl leading-relaxed text-char-600">
          We've run the plants we sell.
        </p>
      </ScrollReveal>
      <ScrollReveal delay={120} className="md:col-span-6 md:col-start-7">
        <div className="divide-y divide-paper/15 border-t border-b border-paper/15">
          {WHY_POINTS.map((p) => (
            <div
              key={p.title}
              data-testid={`home-why-point-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="py-6"
            >
              <div className="font-display text-xl md:text-2xl tracking-tight">{p.title}</div>
              <p className="mt-2 text-char-600 text-[15px] leading-relaxed">{p.body}</p>
            </div>
          ))}
        </div>
        <Link
          to="/about"
          data-testid="home-why-tanren-link"
          className="mt-8 inline-flex items-center gap-2 font-display text-lg hover:text-gold transition-colors"
        >
          Why teams choose Tanren
          <ArrowRight size={18} />
        </Link>
      </ScrollReveal>
    </div>
  </section>
);

const Metrics = () => (
  <section data-testid="home-metrics" className="bg-paper py-20 lg:py-24 border-t border-char-900/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <ScrollReveal>
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-6">
            <h2 className="font-display text-char-900">Proven under load.</h2>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <p className="text-char-700 text-lg leading-relaxed">
              Our founding team has designed, built, commissioned and operated commercial biomass
              plants. Here is what that experience adds up to.
            </p>
          </div>
        </div>
      </ScrollReveal>

      <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-10">
        {metrics.map((m, i) => (
          <ScrollReveal key={m.value} delay={i * 60}>
            <div data-testid={`home-metric-${i + 1}`}>
              <div className="font-display text-gold text-4xl md:text-5xl tracking-tight">
                {m.value}
              </div>
              <div className="mt-3 text-char-700 text-[15px] leading-relaxed max-w-[22ch]">
                {m.label}
              </div>
            </div>
          </ScrollReveal>
        ))}
      </div>

      <ScrollReveal>
        <p className="mt-10 text-char-700 text-[15px] leading-relaxed">
          Figures reflect the combined track record of Tanren's founding team.
        </p>
      </ScrollReveal>
    </div>
  </section>
);

const SolutionsBridge = () => (
  <section
    data-testid="home-solutions-bridge"
    className="bg-paper py-20 lg:py-24 border-t border-char-900/10"
  >
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-12 gap-10 items-end">
      <ScrollReveal className="md:col-span-7">
        <h2 className="font-display text-char-900">
          One engineering partner across the full biomass value chain.
        </h2>
      </ScrollReveal>
      <ScrollReveal delay={120} className="md:col-span-4 md:col-start-9 flex md:justify-end">
        <Link
          to="/services"
          data-testid="home-solutions-bridge-link"
          className="inline-flex items-center gap-2 text-char-900 font-display text-lg hover:text-ember transition-colors"
        >
          Explore Our Capabilities
          <ArrowRight size={18} />
        </Link>
      </ScrollReveal>
    </div>
  </section>
);

const ProcessStrip = () => (
  <section data-testid="home-process" className="bg-paper py-20 lg:py-24 border-t border-char-900/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="grid md:grid-cols-12 gap-10 items-end">
        <div className="md:col-span-6">
          <div className="mono-label">Our Process</div>
          <h2 className="font-display mt-4 text-char-900">
            Five stages. One trajectory.
          </h2>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <p className="text-char-700 text-lg leading-relaxed">
            Every Tanren engagement travels the same route, from characterization to revenue.
          </p>
          <Link
            to="/services"
            data-testid="process-view-approach"
            className="mt-6 inline-flex items-center gap-2 text-char-900 font-display text-lg hover:text-ember transition-colors"
          >
            View the full approach
            <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>

      <div className="mt-16">
        <ProcessLine
          steps={processSteps.map((s) => ({
            number: s.number,
            title: s.title,
          }))}
          variant="light"
          testId="home-process-line"
        />
      </div>
    </div>
  </section>
);

const CtaBanner = () => (
  <section data-testid="home-cta-banner" className="bg-char-900 text-paper relative overflow-hidden">
    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28 grid md:grid-cols-12 gap-10 items-center">
      <div className="md:col-span-8">
        <h2 className="font-display leading-[0.98]">
          Have biomass. <br />
          <span className="text-char-600">Want a bankable facility.</span> <br />
          Let's talk.
        </h2>
      </div>
      <div className="md:col-span-4 flex md:justify-end">
        <Link to="/contact" data-testid="home-cta-banner-btn" className="btn-ember text-base py-4 px-7">
          Start the Conversation
          <ArrowUpRight size={18} />
        </Link>
      </div>
    </div>
  </section>
);

const Home = () => {
  usePageMeta(
    "Tanren | Biomass to Value, Engineered End to End",
    "Tanren engineers pyrolysis, torrefaction, gasification and CBG plants, turning agricultural and forest residue into biochar, bio-oil, bio-coal, CBG, energy and carbon value."
  );
  return (
    <div data-testid="page-home">
      <Hero />
      <TechShowcase />
      <WhyTanren />
      <ProcessStrip />
      <Metrics />
      <SolutionsBridge />
      <CtaBanner />
    </div>
  );
};

export default Home;
